import { Star } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const googleProfileLink = "https://www.google.com/maps/place/Kodiak+Structures+%26+Exteriors";

export type ReviewType = {
  id: string;
  author_name: string;
  author_image: string | null;
  rating: number;
  text: string | null;
  relative_time: string | null;
  review_date: string | null;
  location: string;
};

export const formatReviewDate = (dateString: string | null): string => {
  if (!dateString) return "Recently";
  try { return formatDistanceToNow(new Date(dateString), { addSuffix: true }); } catch { return "Recently"; }
};

export const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < rating ? "fill-yellow-400 text-yellow-400" : "fill-muted text-muted"}`} />
    ))}
  </div>
);

export const ReviewCard = ({ review }: { review: ReviewType }) => {
  const initials = review.author_name.split(" ").map((n) => n[0]).join("").toUpperCase();
  const colors = ["bg-blue-500", "bg-green-500", "bg-purple-500", "bg-orange-500", "bg-pink-500", "bg-teal-500"];
  const color = colors[review.author_name.charCodeAt(0) % colors.length];
  const reviewText = review.text || "";

  return (
    <div className="bg-card border border-border rounded-xl p-6 h-full flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div className="relative">
          {review.author_image ? (
            <img src={review.author_image} alt={review.author_name} className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${color}`}>{initials}</div>
          )}
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-background rounded-full flex items-center justify-center shadow-xs border border-border">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          </div>
        </div>
        <div className="flex-1">
          <span className="font-semibold text-foreground">{review.author_name}</span>
          <p className="text-sm text-muted-foreground">{formatReviewDate(review.review_date || review.relative_time)}</p>
        </div>
      </div>
      <div className="mb-3"><StarRating rating={review.rating} /></div>
      <p className="text-foreground text-sm leading-relaxed flex-1">
        {reviewText.length > 200 ? `${reviewText.slice(0, 200)}...` : reviewText}
      </p>
      {reviewText.length > 200 && (
        <a href={googleProfileLink} target="_blank" rel="noopener noreferrer" className="text-secondary font-medium text-sm mt-3 hover:underline inline-flex items-center gap-1">Read more</a>
      )}
    </div>
  );
};
